import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  // Initialize theme from localStorage or default to 'light'
  const [theme, setTheme] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    return savedTheme || 'light';
  });

  // Apply theme to document root
  // Apply theme to document root
  useEffect(() => {
    const root = document.documentElement;

    // Tailwind expects the class 'dark' to trigger dark mode styles
    if (theme === 'dark') {
      root.classList.add('dark');
      document.body.classList.add('dark'); // Add to body as well for legacy CSS hooks
    } else {
      root.classList.remove('dark');
      document.body.classList.remove('dark');
    }

    // Legacy support (optional, keeping for safety)
    root.classList.remove('light-theme', 'dark-theme');
    root.classList.add(`${theme}-theme`);

    // Also set data attribute for CSS selectors
    root.setAttribute('data-theme', theme);

    // Save to localStorage
    localStorage.setItem('theme', theme);
  }, [theme]);

  // Toggle between light and dark theme
  const toggleTheme = () => {
    setTheme(prevTheme => prevTheme === 'light' ? 'dark' : 'light');
  };

  // Set specific theme
  const setThemeMode = (mode) => {
    if (mode === 'light' || mode === 'dark') {
      setTheme(mode);
    }
  };

  const value = {
    theme,
    toggleTheme,
    setThemeMode,
    isDark: theme === 'dark'
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
